<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Tarefas Atrasadas por Projeto</h3>
            <GraficoInfoIcon 
                titulo="Tarefas Atrasadas por Projeto"
                descricao="Mostra a quantidade de tarefas atrasadas em cada projeto, ajudando a identificar projetos com maior risco de descumprimento de prazos."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoTarefasAtrasadasPorProjeto',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        projetosComTarefasAtrasadas() {
            return this.projetos
                .filter(p => (p.estatisticas?.tarefas?.atrasadas ?? 0) > 0)
                .sort((a, b) => (b.estatisticas?.tarefas?.atrasadas ?? 0) - (a.estatisticas?.tarefas?.atrasadas ?? 0))
                .slice(0, 20);
        },
        temDados() {
            return this.projetosComTarefasAtrasadas.length > 0;
        },
        chartData() {
            const projetos = this.projetosComTarefasAtrasadas;
            return {
                labels: projetos.map(p => {
                    const codigoPCM = p.pcm?.codigo || '';
                    const nome = p.nome || 'Sem nome';
                    const label = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
                    return label.length > 25 ? label.substring(0, 25) + '...' : label;
                }),
                datasets: [{
                    label: 'Tarefas Atrasadas',
                    data: projetos.map(p => p.estatisticas?.tarefas?.atrasadas ?? 0),
                    backgroundColor: 'rgba(231, 76, 60, 0.6)',
                    borderColor: 'rgba(231, 76, 60, 1)',
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const projeto = this.projetosComTarefasAtrasadas[index];
                        if (projeto) {
                            self.$emit('clicar-projeto', projeto.id);
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Quantidade de Tarefas Atrasadas'
                        },
                        ticks: {
                            stepSize: 1
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const projeto = this.projetosComTarefasAtrasadas[context.dataIndex];
                                const total = projeto?.estatisticas?.tarefas?.total ?? 0;
                                return [
                                    `Tarefas Atrasadas: ${context.parsed.x}`,
                                    `Total de Tarefas: ${total}`
                                ];
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

